/**
 * The channel-aware derive rules (ADR-020 §3), as pure math over planar
 * Float32 channels — the part naive resizing gets wrong, measured in T1 (§9):
 *
 *   albedo/emissive  Lanczos in LINEAR space (decode sRGB first — naive sRGB
 *                    filtering measured Δ1.05/255 against this)
 *   roughness        plain Lanczos — standard practice
 *   metallic         Lanczos then RE-THRESHOLD — metal is semantically binary;
 *                    the master's "empty" channel is a 47.7% noise floor that
 *                    re-threshold cleans to 0 while a painted mask survives
 *   normal           Lanczos then renormalize each texel to unit length
 *                    (5.3% come out off-unit from the naive filter)
 *
 * The Lanczos-3 here is Pillow-compatible separable resampling (the T1
 * prototype's reference implementation), so the §9 numbers transfer.
 * Depends on: nothing. Depended on by: derive-glb, tests, the T2 probe.
 */

const SUPPORT = 3; // Lanczos-3

const lanczos3 = (x: number): number => {
  if (x === 0) return 1;
  const ax = Math.abs(x);
  if (ax >= SUPPORT) return 0;
  const px = Math.PI * x;
  return (SUPPORT * Math.sin(px) * Math.sin(px / SUPPORT)) / (px * px);
};

interface WeightTable {
  readonly bounds: Int32Array; // per dst index: [first src index, tap count]
  readonly weights: Float64Array; // packed taps, normalized to sum 1
  readonly maxTaps: number;
}

/** Precompute one axis's contribution table (identical for every row/column). */
function weightTable(srcSize: number, dstSize: number): WeightTable {
  const scale = srcSize / dstSize; // > 1 when downscaling
  const filterscale = Math.max(scale, 1);
  const span = SUPPORT * filterscale;
  const maxTaps = Math.ceil(span * 2) + 2;
  const bounds = new Int32Array(dstSize * 2);
  const weights = new Float64Array(dstSize * maxTaps);
  for (let i = 0; i < dstSize; i += 1) {
    const center = (i + 0.5) * scale;
    let first = Math.floor(center - span);
    let last = Math.ceil(center + span);
    if (first < 0) first = 0;
    if (last > srcSize) last = srcSize;
    let sum = 0;
    const taps = last - first;
    for (let j = 0; j < taps; j += 1) {
      const w = lanczos3((first + j + 0.5 - center) / filterscale);
      weights[i * maxTaps + j] = w;
      sum += w;
    }
    if (sum !== 0) {
      for (let j = 0; j < taps; j += 1) weights[i * maxTaps + j] = (weights[i * maxTaps + j] as number) / sum;
    }
    bounds[i * 2] = first;
    bounds[i * 2 + 1] = taps;
  }
  return { bounds, weights, maxTaps };
}

/**
 * Resize ONE planar channel with separable Lanczos-3. Never upscales — the
 * derive law (L3) only moves down; a larger destination throws.
 */
export function resizePlane(
  src: Float32Array,
  srcWidth: number,
  srcHeight: number,
  dstWidth: number,
  dstHeight: number,
): Float32Array {
  if (dstWidth > srcWidth || dstHeight > srcHeight) {
    throw new Error(
      `resizePlane refuses to upscale (${srcWidth}×${srcHeight} → ${dstWidth}×${dstHeight}) — deriving is downward only (ADR-020 L3)`,
    );
  }
  if (dstWidth === srcWidth && dstHeight === srcHeight) return src.slice();

  // Horizontal pass: (srcWidth × srcHeight) → (dstWidth × srcHeight)
  const hTable = weightTable(srcWidth, dstWidth);
  const mid = new Float32Array(dstWidth * srcHeight);
  for (let y = 0; y < srcHeight; y += 1) {
    const rowBase = y * srcWidth;
    const outBase = y * dstWidth;
    for (let x = 0; x < dstWidth; x += 1) {
      const first = hTable.bounds[x * 2] as number;
      const taps = hTable.bounds[x * 2 + 1] as number;
      const wBase = x * hTable.maxTaps;
      let acc = 0;
      for (let j = 0; j < taps; j += 1) {
        acc += (src[rowBase + first + j] as number) * (hTable.weights[wBase + j] as number);
      }
      mid[outBase + x] = acc;
    }
  }

  // Vertical pass: (dstWidth × srcHeight) → (dstWidth × dstHeight)
  const vTable = weightTable(srcHeight, dstHeight);
  const dst = new Float32Array(dstWidth * dstHeight);
  for (let y = 0; y < dstHeight; y += 1) {
    const first = vTable.bounds[y * 2] as number;
    const taps = vTable.bounds[y * 2 + 1] as number;
    const wBase = y * vTable.maxTaps;
    const outBase = y * dstWidth;
    for (let x = 0; x < dstWidth; x += 1) {
      let acc = 0;
      for (let j = 0; j < taps; j += 1) {
        acc += (mid[(first + j) * dstWidth + x] as number) * (vTable.weights[wBase + j] as number);
      }
      dst[outBase + x] = acc;
    }
  }
  return dst;
}

/** sRGB byte → linear float, via the exact transfer curve (LUT, 256 entries). */
const SRGB_TO_LINEAR = ((): Float32Array => {
  const lut = new Float32Array(256);
  for (let i = 0; i < 256; i += 1) {
    const c = i / 255;
    lut[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }
  return lut;
})();

export function srgbPlaneToLinear(bytes: Float32Array): Float32Array {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) out[i] = SRGB_TO_LINEAR[Math.round(bytes[i] as number)] as number;
  return out;
}

export function linearPlaneToSrgbBytes(linear: Float32Array): Float32Array {
  const out = new Float32Array(linear.length);
  for (let i = 0; i < linear.length; i += 1) {
    const c = Math.min(1, Math.max(0, linear[i] as number));
    const s = c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
    out[i] = Math.round(s * 255);
  }
  return out;
}

/** Metallic's rule: filtered bytes → binary (≥128 is metal). */
export function rethresholdMetallic(plane: Float32Array): Float32Array {
  const out = new Float32Array(plane.length);
  for (let i = 0; i < plane.length; i += 1) out[i] = (plane[i] as number) >= 128 ? 255 : 0;
  return out;
}

/**
 * Normal's rule: three filtered byte-planes → renormalized byte-planes.
 * Each texel decodes to a vector ([0,255] → [-1,1]), is scaled to unit
 * length, and re-encodes; a degenerate (≈zero) vector re-encodes as +Z.
 */
export function renormalizeNormals(
  x: Float32Array,
  y: Float32Array,
  z: Float32Array,
): { x: Float32Array; y: Float32Array; z: Float32Array } {
  const ox = new Float32Array(x.length);
  const oy = new Float32Array(x.length);
  const oz = new Float32Array(x.length);
  for (let i = 0; i < x.length; i += 1) {
    const vx = ((x[i] as number) / 255) * 2 - 1;
    const vy = ((y[i] as number) / 255) * 2 - 1;
    const vz = ((z[i] as number) / 255) * 2 - 1;
    const len = Math.sqrt(vx * vx + vy * vy + vz * vz);
    const nx = len > 1e-6 ? vx / len : 0;
    const ny = len > 1e-6 ? vy / len : 0;
    const nz = len > 1e-6 ? vz / len : 1;
    ox[i] = Math.round(((nx + 1) / 2) * 255);
    oy[i] = Math.round(((ny + 1) / 2) * 255);
    oz[i] = Math.round(((nz + 1) / 2) * 255);
  }
  return { x: ox, y: oy, z: oz };
}

/** Multiply a byte-plane by a factor, clamped to [0,255] — the roughness bake. */
export function scalePlane(plane: Float32Array, factor: number): Float32Array {
  const out = new Float32Array(plane.length);
  for (let i = 0; i < plane.length; i += 1) {
    out[i] = Math.min(255, Math.max(0, Math.round((plane[i] as number) * factor)));
  }
  return out;
}

/**
 * Replace a byte-plane with a CONSTANT — the channel-detach bake.
 *
 * glTF keeps roughness in G and metallic in B of one metallicRoughness
 * texture and reads BOTH channels from it whenever it is present, so a
 * surface that wants "metallic from the map, roughness a flat value" cannot
 * say so as a material property. It has to be written into the plane. This is
 * that write: the other channel's map survives untouched beside it.
 */
export function fillPlane(plane: Float32Array, value: number): Float32Array {
  const byte = Math.min(255, Math.max(0, Math.round(value)));
  const out = new Float32Array(plane.length);
  out.fill(byte);
  return out;
}

/** Round a filtered byte-plane for encoding (plain rule's finishing step). */
export function roundPlane(plane: Float32Array): Float32Array {
  const out = new Float32Array(plane.length);
  for (let i = 0; i < plane.length; i += 1) out[i] = Math.min(255, Math.max(0, Math.round(plane[i] as number)));
  return out;
}

/** Split interleaved 8-bit pixels into planar Float32 channels. */
export function toPlanes(data: Uint8Array, channels: number): Float32Array[] {
  const n = data.length / channels;
  const planes = Array.from({ length: channels }, () => new Float32Array(n));
  for (let i = 0; i < n; i += 1) {
    for (let c = 0; c < channels; c += 1) (planes[c] as Float32Array)[i] = data[i * channels + c] as number;
  }
  return planes;
}

/** Interleave planar byte-valued Float32 channels back to 8-bit pixels. */
export function fromPlanes(planes: readonly Float32Array[]): Uint8Array {
  const channels = planes.length;
  const n = (planes[0] as Float32Array).length;
  const out = new Uint8Array(n * channels);
  for (let i = 0; i < n; i += 1) {
    for (let c = 0; c < channels; c += 1) out[i * channels + c] = (planes[c] as Float32Array)[i] as number;
  }
  return out;
}
